import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { formatDate } from '@angular/common';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
} from '@angular/material/dialog';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';
import { BehaviorSubject, Subscription } from 'rxjs';

import { AppState } from '../../../../../core/reducers';
import { LayoutUtilsService, MessageType } from '../../../../../core/_base/crud';
import { StringUtilsService } from '../../../../../core/services/string-utils.service';
import {
  ContactModel,
  ContactRelationshipsService,
  RelationshipCategory,
  RelationshipType,
} from '../../../../../core/lists';
import { GeneralAutocompleteComponent } from '../../../../partials/layout/general-autocomplete/general-autocomplete.component';

import { Category, Document, Entity, EntityRelationFilter } from '../../models';
import {
  AccessHistoryType,
  AccessHistoryUserType,
  DocType,
  EntityType,
  ReviewMode,
} from '../../enums';
import { CreateDocumentOnServer, UpdateDocumentOnServer } from '../../actions';
import { DocumentService } from '../../services';
import { AssignEntityComponent } from '../assign-entity/assign-entity.component';
import { SelectRelationComponent } from '../select-relation/select-relation.component';
import { ShareDocumentComponent } from '../share-document/share-document.component';
import { ConfirmActionDialogComponent } from '../../../../partials/content/crud';

@Component({
  selector: 'kt-create-document',
  templateUrl: './create-document.component.html',
  styleUrls: ['./create-document.component.scss'],
})
export class CreateDocumentComponent implements AfterViewInit, OnInit, OnDestroy {
  private subscriptions = new Subscription();
  form: FormGroup;
  DocType = DocType;
  ReviewMode = ReviewMode;
  docTypes = Object.values(DocType);
  selectedEntityTypes = [];
  submitted = false;
  showTypeSelect = false;
  showNameInput = false;
  showCategoryAutocomplete = false;
  showChangeBtn = false;
  showSrcLayout = false;
  showMsgLayout = false;
  entitiesString$ = new BehaviorSubject<string>('');
  relationsString$ = new BehaviorSubject<string>('');
  downloadReasonCtrl = new FormControl('', Validators.required);
  getCategoryAutocomplete = this.documentService.findCategories.bind(this.documentService, {});
  getContactAutocomplete;
  @ViewChild('toggleType') toggleType: ElementRef;
  @ViewChild('toggleName') toggleName: ElementRef;
  @ViewChild('toggleCategory') toggleCategory: ElementRef;
  @ViewChild('typeSelect') typeSelect: MatSelect;
  @ViewChild('nameInput') nameInput: ElementRef;
  @ViewChild('categoryAutocomplete') categoryAutocomplete: GeneralAutocompleteComponent;
  @ViewChild('contactAutocomplete') contactAutocomplete: GeneralAutocompleteComponent;
  valuesChanged = false;
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { document: Document; viewMode: boolean },
    public stringUtils: StringUtilsService,
    private dialogRef: MatDialogRef<CreateDocumentComponent>,
    private dialog: MatDialog,
    private store: Store<AppState>,
    private formBuilder: FormBuilder,
    private translate: TranslateService,
    private layoutUtils: LayoutUtilsService,
    private documentService: DocumentService,
    private contactRelationshipsService: ContactRelationshipsService,
  ) {}

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      referenceId: [this.data.document?.reference_id],
      category: [this.data.document?.category],
      type: [this.data.document?.type, Validators.required],
      name: [this.data.document?.name, Validators.required],
      url: [this.data.document?.url, Validators.required],
      file: [this.data.document?.file_base64, this.data.document?.id ? [] : Validators.required],
      description: [this.data.document?.description, Validators.required],
      entities: [null, Validators.required],
      relationCategories: [],
      relationTypes: [],
      phi: [],
      patientOnly: [],
      mobileVisible: [],
      requiresReview: [!!this.data.document?.requires_review],
      primaryReviewer: [!!this.data.document?.primary_reviewer],
      reviewMode: [this.data.document?.review_mode],
      reviewByDate: [
        this.data.document?.review_by_date && new Date(this.data.document.review_by_date),
      ],
      visibleAfter: [
        this.data.document?.visible_after && new Date(this.data.document.visible_after),
      ],
      expiryAfter: [this.data.document?.expire_after && new Date(this.data.document.expire_after)],
    });
    this.form.valueChanges.subscribe(() => {
      if (this.form.dirty) {
        this.valuesChanged = true;
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.data.document?.id) {
      this.categoryAutocomplete?.setValue(this.data.document.category);
      this.categoryAutocomplete?.updateValueAndValidity();
      this.contactAutocomplete?.setValue(this.data.document.contact_reviewers || []);
      this.contactAutocomplete?.updateValueAndValidity();

      this.handleDocTypeChange(this.data.document.type);
      this.handleRequiresReviewToggle(this.data.document.requires_review);

      this.form.get('phi').patchValue(!!this.data.document?.phi);
      this.form.get('patientOnly').patchValue(!!this.data.document?.patient_only);
      this.form.get('mobileVisible').patchValue(!!this.data.document?.mobile_visible);

      if (this.data.document.entities?.length) {
        this.form.get('entities').patchValue(this.data.document.entities);
        this.entitiesString$.next(this.formatEntities(this.data.document.entities));
      }

      if (this.data.document.relation_categories?.length) {
        this.form.get('relationCategories').patchValue(this.data.document.relation_categories);
        this.form.get('relationTypes').patchValue(this.data.document.relation_types);
        this.relationsString$.next(
          this.formatRelations(
            this.data.document.relation_categories,
            this.data.document.relation_types,
          ),
        );
      }

      if (this.data.viewMode)
        Object.keys(this.form.controls).forEach((key) => this.form.get(key).disable());
    }

    if (this.data.document?.entity_id)
      this.contactRelationshipsService
        .getEntitiesById([this.data.document.entity_id])
        .pipe(take(1))
        .subscribe((res) => {
          const entities = res?.data;

          if (!entities?.length) return;

          if (!this.data.document?.id) {
            this.form.get('entities').patchValue(entities);
            this.entitiesString$.next(this.formatEntities(entities));
          }

          this.getCategoryAutocomplete = this.documentService.findCategories.bind(
            this.documentService,
            {
              entityTypes: entities.map((e) => e.entity_type),
            },
          );
        });

    this.subscriptions.add(
      this.contactAutocomplete?.focusInBS.subscribe(() => {
        const formValue = this.form.value;
        const filter: Partial<EntityRelationFilter> = {
          entityIds: formValue.entities?.map((entity) => entity.id),
          relationCategoryIds: formValue.relationCategories?.map((category) => category.id),
          relationTypeIds: formValue.relationTypes?.map((type) => type.id),
          phi: formValue.phi,
          patientOnly: formValue.patientOnly,
          invoice: formValue.type === DocType.INVOICE,
        };

        this.getContactAutocomplete =
          this.contactRelationshipsService.findEntitiesRelationsAutocomplete.bind(
            this.contactRelationshipsService,
            filter,
          );
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  @HostListener('click', ['$event'])
  handleClick(event: Event): void {
    if (!this.data.viewMode) {
      if (this.form.get('type').value === DocType.VIDEO_LINK) {
        this.showNameInput =
          event.target === this.toggleName?.nativeElement ||
          event.target === this.nameInput?.nativeElement;
      }

      this.showTypeSelect =
        event.target === this.toggleType?.nativeElement ||
        event.target === this.typeSelect?._elementRef?.nativeElement;
      this.showCategoryAutocomplete =
        event.target === this.toggleCategory?.nativeElement ||
        event.target === this.categoryAutocomplete?.recorderInput?.nativeElement;
    }
  }

  handleDocTypeChange(type: DocType): void {
    if (type === DocType.VIDEO_LINK) {
      if (this.form.get('file')) this.form.removeControl('file');
      if (!this.form.get('url'))
        this.form.addControl('url', new FormControl(this.data.document?.url, Validators.required));
    } else {
      if (this.form.get('url')) this.form.removeControl('url');
      if (!this.form.get('file'))
        this.form.addControl(
          'file',
          new FormControl(this.data.document?.file_base64, Validators.required),
        );
    }

    if (type === DocType.INVOICE) {
      this.form.get('phi').patchValue(false);
      this.form.get('patientOnly').patchValue(false);
      this.form.get('relationCategories').patchValue([]);
      this.form.get('relationTypes').patchValue([]);
      this.contactAutocomplete?.setValue([]);
      this.contactAutocomplete?.updateValueAndValidity();

      this.relationsString$.next('');
    }
  }

  handleRequiresReviewToggle(required: boolean): void {
    if (required) {
      this.form.get('reviewMode').setValidators(Validators.required);
      this.form.get('reviewMode').updateValueAndValidity();
      this.form.get('reviewByDate').enable();
    } else {
      this.form.get('reviewMode').setValidators(null);
      this.form.get('reviewMode').patchValue(null);
      this.form.get('reviewMode').updateValueAndValidity();
      this.form.get('reviewByDate').patchValue('');
      this.contactAutocomplete?.setValue([]);
      this.contactAutocomplete?.updateValueAndValidity();

      this.form.get('reviewByDate').disable();
    }
  }

  handleReviewerToggle(isPrimary: boolean): void {
    if (isPrimary) {
      this.form.get('relationCategories').patchValue([]);
      this.form.get('relationTypes').patchValue([]);
      this.contactAutocomplete?.setValue([]);
      this.contactAutocomplete?.updateValueAndValidity();

      this.relationsString$.next('');
    }
  }

  assignEntities(event: Event): void {
    event.stopPropagation();

    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.width = '600px';
    dialogConfig.data = {
      entityTypes: this.categoryAutocomplete.value?.entity_types.map((type) => type.entity_type),
      entities: this.form.get('entities').value,
    };

    const dialogRef = this.dialog.open(AssignEntityComponent, dialogConfig);

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) return;

        this.form.get('entities').patchValue(res.entities);
        this.form.get('relationCategories').patchValue([]);
        this.form.get('relationTypes').patchValue([]);
        this.contactAutocomplete?.setValue([]);
        this.contactAutocomplete?.updateValueAndValidity();

        this.entitiesString$.next(this.formatEntities(res.entities));
        this.relationsString$.next('');
      });
  }

  selectRelations(event: Event): void {
    event.stopPropagation();

    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.width = '600px';
    dialogConfig.data = {
      entityTypes: this.selectedEntityTypes,
      relationCategories: this.form.get('relationCategories').value,
      relationTypes: this.form.get('relationTypes').value,
    };

    const dialogRef = this.dialog.open(SelectRelationComponent, dialogConfig);

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) return;

        this.form.get('relationCategories').patchValue(res.relationCategories);
        this.form.get('relationTypes').patchValue(res.relationTypes);
        this.contactAutocomplete?.setValue([]);
        this.contactAutocomplete?.updateValueAndValidity();

        this.relationsString$.next(this.formatRelations(res.relationCategories, res.relationTypes));
      });
  }

  formatEntities(entities: Entity[]): string {
    const entitiesMap = {};
    let entitiesStr = '';

    this.selectedEntityTypes = [];

    Object.values(EntityType).forEach((type) => (entitiesMap[type] = []));
    entities?.forEach((entity) => entitiesMap[entity.entity_type].push(entity));

    for (const [key, value] of Object.entries(entitiesMap)) {
      if ((value as Entity[]).length) {
        this.selectedEntityTypes.push(key);

        entitiesStr += `
          <div class="d-flex mb-2">
            <div style="width: 75px !important" class="font-weight-bold">${this.stringUtils.formatEnumString(
              key,
            )}:</div>
            <div class="flex-grow-1 d-flex flex-column">${(value as Entity[])
              .map(
                (entity) =>
                  `<small class="text-black-50">${
                    entity.name || (entity.first_name || '') + ' ' + (entity.last_name || '')
                  }</small>`,
              )
              .join('')}</div>
          </div>
        `;
      }
    }

    return entitiesStr;
  }

  formatRelations(
    relationCategories: RelationshipCategory[],
    relationTypes: RelationshipType[],
  ): string {
    const relationsMap = {};
    let relationsStr = '';

    relationCategories.forEach((category) => (relationsMap[category.entity_type] = {}));
    relationCategories.forEach(
      (category) => (relationsMap[category.entity_type][category.name] = []),
    );
    relationTypes?.forEach((relationType) =>
      relationsMap[relationType.category.entity_type][relationType.category.name].push(
        relationType,
      ),
    );

    Object.keys(relationsMap).forEach((type) => {
      relationsStr += `
        <div class="d-flex mb-2">
          <div style="width: 75px !important" class="font-weight-bold">${this.stringUtils.formatEnumString(
            type,
          )}:</div>
          <div class="flex-grow-1 d-flex flex-column">${Object.entries(relationsMap[type])
            .map(
              ([key, value]) => `
              <small class="d-flex align-items-start text-black-50 mb-1">
                <span>${key}</span>
                <span class="flex-grow-1 d-flex flex-column">
                  ${(value as RelationshipType[])
                    .map(
                      (relationType) =>
                        `<span><span class="px-2">></span>${
                          relationType.description_type || ''
                        }</span>`,
                    )
                    .join('')}
                </span>
              </small>
            `,
            )
            .join('')}</div>
        </div>
      `;
    });

    return relationsStr;
  }

  formatCategoryOption(category: Category): string {
    return category?.name || '';
  }

  handleCategoryChange(category: Category): void {
    this.form.get('category').patchValue(category);
    this.form.get('phi').patchValue(!!category?.phi);
    this.form.get('patientOnly').patchValue(!!category?.patient_only);
    this.form.get('mobileVisible').patchValue(!!category?.mobile_visible);

    if (this.data.document?.entity_id) return;

    this.form.get('entities').patchValue([]);
    this.form.get('relationCategories').patchValue([]);
    this.form.get('relationTypes').patchValue([]);
    this.contactAutocomplete?.setValue([]);
    this.contactAutocomplete?.updateValueAndValidity();

    this.entitiesString$.next('');
    this.relationsString$.next('');
  }

  formatContactOption(contact: ContactModel): string {
    return `${contact?.first_name || ''} ${contact?.middle_name || ''} ${
      contact?.last_name || ''
    } ${contact?.last_name2 || ''}`.trim();
  }

  formatNewContact(contact: ContactModel): ContactModel {
    return contact;
  }

  clickFileInput(event: Event, fileInput: HTMLInputElement): void {
    event.stopPropagation();

    if (!this.data.viewMode) {
      fileInput.click();
      this.form.get('file').markAsTouched();
    }
  }

  changeFile(event: Event): void {
    const target = event.target as HTMLInputElement;

    if (target?.files?.length) {
      const file = target.files[0];
      const reader = new FileReader();

      reader.readAsDataURL(file);
      reader.onload = () => {
        this.form.get('name').patchValue(file.name.replace(/\.[0-9a-z]+$/i, ''));
        this.form.get('file').patchValue((reader.result as string)?.split(',')[1]);
        this.showMsgLayout = true;
      };
    }
  }

  downloadFile(event: Event): void {
    event.stopPropagation();

    this.downloadReasonCtrl.markAsTouched();

    if (this.downloadReasonCtrl.invalid) return;

    this.documentService
      .openDocumentSession(this.data.document?.id, {
        user_type: AccessHistoryUserType.USER,
        interaction_type: AccessHistoryType.DOWNLOAD,
        download_reason: this.downloadReasonCtrl.value,
      })
      .pipe(take(1))
      .subscribe((res) => {
        if (!res?.data) return;

        window.open(this.data.document?.url, '_blank').focus();
      });
  }

  shareDocument(event: Event): void {
    event.stopPropagation();

    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.closeOnNavigation = true;
    dialogConfig.width = '800px';
    dialogConfig.data = this.data.document;

    const dialogRef = this.dialog.open(ShareDocumentComponent, dialogConfig);

    dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe((res) => {
        if (!res) return;

        this.layoutUtils.showActionNotification(
          this.translate.instant('DOCS_MODULE.DOCUMENT.SHARE_MESSAGE'),
          MessageType.Create,
          3000,
          false,
          false,
        );
      });
  }

  toggleShowLayout(event: Event): void {
    event.stopPropagation();

    this.showSrcLayout = !this.showSrcLayout;
  }

  undoSrcChange(event: Event): void {
    this.toggleShowLayout(event);
    this.showMsgLayout = false;

    if (this.form.get('type').value === DocType.VIDEO_LINK) {
      this.form.get('url').patchValue(this.data.document?.url);
    } else {
      this.form.get('file').patchValue(this.data.document?.file_base64);
      this.form.get('name').patchValue(this.data.document?.name);
    }
  }

  getTypeClass(): string {
    switch (this.form.get('type').value) {
      case DocType.PDF:
      case DocType.INVOICE:
        return 'label-light-danger';

      case DocType.TIF:
      case DocType.TIFF:
        return 'label-light-info';

      case DocType.HTML:
        return 'label-light-warning';

      case DocType.VIDEO_LINK:
        return 'label-light-primary';
    }
  }

  getTitle(): string {
    return this.data.document?.id
      ? this.form.get('name').value
      : 'DOCS_MODULE.DOCUMENT.CREATE_TITLE';
  }

  isInvalid(field: string | FormControl): boolean {
    const control = typeof field === 'string' ? this.form.get(field) : field;

    return control.touched && control.invalid;
  }

  getErrorMessage(label: string): string {
    return `${label} field is required`;
  }

  closeDialog(event: Event): void {
    event.stopPropagation();
    this.valuesChanged === true ? this.ConfirmClose() : this.dialogRef.close();
  }
  ConfirmClose() {
    const cofirmActionDialogRef = this.dialog.open(ConfirmActionDialogComponent, {
      data: {
        title: 'Close Task Information ',
        description: 'If you close you will lose the changes you have made.',
        waitDesciption: 'Loading...',
      },
      width: '440px',
    });
    cofirmActionDialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      this.dialogRef.close();
    });
  }
  cancel(event: Event): void {
    event.stopPropagation();

    this.dialogRef.close();
  }

  submit(event: Event): void {
    event.stopPropagation();

    this.form.markAllAsTouched();
    this.categoryAutocomplete.updateValueAndValidity();

    if (this.form.invalid || this.categoryAutocomplete.isInvalid()) return;

    this.submitted = true;

    const formValue = this.form.value;
    const document = new Document(
      this.data.document?.id,
      this.data.document?.entity_id,
      formValue.referenceId,
      formValue.type,
      this.categoryAutocomplete.value,
      formValue.name,
      formValue.description,
      formValue.file,
      formValue.url,
      formValue.entities,
      formValue.relationCategories,
      formValue.relationTypes,
      formValue.phi,
      formValue.patientOnly,
      formValue.mobileVisible,
      formValue.requiresReview,
      formValue.primaryReviewer,
      formValue?.reviewMode,
      this.contactAutocomplete?.value,
      formValue.reviewByDate,
      formValue.visibleAfter
        ? `${formatDate(formValue.visibleAfter, 'yyyy-MM-dd', 'en-US')}T23:59:59.000Z`
        : '',
      formValue.expiryAfter
        ? `${formatDate(formValue.expiryAfter, 'yyyy-MM-dd', 'en-US')}T23:59:59.000Z`
        : '',
    );

    for (const [key, value] of Object.entries(document)) {
      if (typeof value === 'string') document[key] = value.trim();
    }
    if (!this.data.document?.id) {
      delete document.id;
    }
    this.data.document?.id
      ? this.store.dispatch(
          new UpdateDocumentOnServer({
            document,
            close: this.dialogRef.close.bind(this.dialogRef),
          }),
        )
      : this.store.dispatch(
          new CreateDocumentOnServer({
            document,
            close: this.dialogRef.close.bind(this.dialogRef),
          }),
        );
  }
}
